import { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Loader2, Building2, Save, LogOut, CheckCircle2, AlertTriangle, Clock,
  CreditCard, ExternalLink, X,
} from 'lucide-react';
import { useAuth, formatApiErrorDetail } from '../context/AuthContext';
import api from '../lib/api';

function StatusBanner({ user, onAssinar, onCancelar, criandoAssinatura, cancelando }) {
  if (!user) return null;
  const expira = user.assinaturaExpiraEm ? new Date(user.assinaturaExpiraEm) : null;
  const agora = new Date();
  const diasRestantes = expira ? Math.ceil((expira - agora) / 86400000) : 0;
  const trial = user.plano === 'trial';
  const ativo = user.plano && user.plano !== 'trial' && user.plano !== 'cancelado' && diasRestantes > 0;
  const expirado = !ativo && !trial && diasRestantes <= 0;
  const cancelado = user.plano === 'cancelado';

  if (trial && diasRestantes > 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3" data-testid="anunc-status-trial">
        <Clock size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-900">Período de teste — {diasRestantes} dia{diasRestantes !== 1 ? 's' : ''} restantes</p>
          <p className="text-xs text-amber-800 mt-0.5">Aproveite os 60 dias grátis. Após esse prazo, R$ 79,90/mês (cartão).</p>
        </div>
        <button onClick={onAssinar} disabled={criandoAssinatura} data-testid="anunc-assinar-btn" className="text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-3 py-1.5 flex items-center gap-1 disabled:opacity-60">
          {criandoAssinatura ? <Loader2 size={12} className="animate-spin" /> : <CreditCard size={12} />} Assinar agora
        </button>
      </div>
    );
  }
  if (ativo) {
    return (
      <div className="bg-[#E1F5EE] border border-[#1D9E75]/30 rounded-2xl p-4 flex items-start gap-3" data-testid="anunc-status-ativo">
        <CheckCircle2 size={18} className="text-[#1D9E75] mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#0F6E56]">Assinatura ativa · {diasRestantes} dia{diasRestantes !== 1 ? 's' : ''} restantes</p>
          <p className="text-xs text-[#0F6E56]/80 mt-0.5">Plano: R$ 79,90/mês. Renova automaticamente.</p>
        </div>
        <button onClick={onCancelar} disabled={cancelando} data-testid="anunc-cancelar-btn" className="text-xs font-semibold border border-red-300 text-red-700 hover:bg-red-50 rounded-lg px-3 py-1.5 flex items-center gap-1 disabled:opacity-60">
          {cancelando ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />} Cancelar
        </button>
      </div>
    );
  }
  if (cancelado || expirado) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3" data-testid={cancelado ? 'anunc-status-cancelado' : 'anunc-status-expirado'}>
        <AlertTriangle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-red-900">{cancelado ? 'Assinatura cancelada' : 'Assinatura expirada'}</p>
          <p className="text-xs text-red-800 mt-0.5">Reative sua assinatura para voltar a anunciar. R$ 79,90/mês.</p>
        </div>
        <button onClick={onAssinar} disabled={criandoAssinatura} data-testid="anunc-reativar-btn" className="text-xs font-semibold bg-red-600 hover:bg-red-700 text-white rounded-lg px-3 py-1.5 flex items-center gap-1 disabled:opacity-60">
          {criandoAssinatura ? <Loader2 size={12} className="animate-spin" /> : <CreditCard size={12} />} Reativar
        </button>
      </div>
    );
  }
  return null;
}

export default function AnuncianteDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, updateUser, logout, loading } = useAuth();
  const [form, setForm] = useState(null);
  const [tipos, setTipos] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [ok, setOk] = useState('');
  const [criandoAssinatura, setCriandoAssinatura] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [aguardandoConfirmacao, setAguardandoConfirmacao] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        nomeFantasia: user.nomeFantasia || user.nome || '',
        razaoSocial: user.razaoSocial || '',
        cnpj: user.cnpj || '',
        whatsapp: user.whatsapp || user.telefone || '',
        site: user.site || '',
        tipoEmpresa: user.tipoEmpresa || '',
        cep: user.cep || '',
        rua: user.rua || '',
        numero: user.numero || '',
        complemento: user.complemento || '',
        bairro: user.bairro || '',
        cidade: user.cidade || '',
        estado: user.estado || '',
      });
    }
  }, [user]);

  useEffect(() => {
    api.get('/tipos-empresa').then(r => setTipos(r.data)).catch(() => {});
  }, []);

  // Polling de status da assinatura quando voltando do MP (subscription=success)
  useEffect(() => {
    if (searchParams.get('subscription') !== 'success') return;
    setAguardandoConfirmacao(true);

    let tries = 0;
    const interval = setInterval(async () => {
      tries += 1;
      try {
        const res = await api.get('/anunciante/assinatura/status');
        if (res.data.status === 'authorized') {
          // recarrega o usuário
          const me = await api.get('/auth/anunciante/me');
          updateUser(me.data);
          setOk('Assinatura ativada com sucesso!');
          setAguardandoConfirmacao(false);
          setSearchParams({});
          clearInterval(interval);
          setTimeout(() => setOk(''), 4000);
        }
      } catch { /* ignore */ }
      if (tries >= 24) {  // ~2 minutos
        setAguardandoConfirmacao(false);
        clearInterval(interval);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [searchParams, setSearchParams, updateUser]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onCepBlur = async () => {
    const cep = (form?.cep || '').replace(/\D/g, '');
    if (cep.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const d = await res.json();
      if (!d.erro) {
        setForm(f => ({
          ...f,
          rua: d.logradouro || f.rua,
          bairro: d.bairro || f.bairro,
          cidade: d.localidade || f.cidade,
          estado: d.uf || f.estado,
        }));
      }
    } catch { /* ignore */ }
  };

  const salvar = async (e) => {
    e.preventDefault();
    setErro(''); setOk(''); setSalvando(true);
    try {
      const res = await api.patch('/anunciante/me', form);
      updateUser(res.data);
      setOk('Perfil atualizado.');
      setTimeout(() => setOk(''), 2500);
    } catch (err) {
      setErro(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally { setSalvando(false); }
  };

  const sair = () => { logout(); navigate('/'); };

  const handleAssinar = async () => {
    setErro(''); setCriandoAssinatura(true);
    try {
      const res = await api.post('/anunciante/assinatura/criar');
      if (res.data.ja_ativa) {
        setOk('Você já tem uma assinatura ativa.');
        return;
      }
      if (res.data.init_point) {
        // Redireciona para o checkout do Mercado Pago
        window.location.href = res.data.init_point;
      } else {
        setErro('Não foi possível abrir o checkout do Mercado Pago.');
      }
    } catch (err) {
      setErro(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setCriandoAssinatura(false);
    }
  };

  const handleCancelarAssinatura = async () => {
    if (!window.confirm('Tem certeza que deseja cancelar sua assinatura? Você perderá acesso ao final do período já pago.')) return;
    setErro(''); setCancelando(true);
    try {
      await api.post('/anunciante/assinatura/cancelar');
      const me = await api.get('/auth/anunciante/me');
      updateUser(me.data);
      setOk('Assinatura cancelada.');
      setTimeout(() => setOk(''), 4000);
    } catch (err) {
      setErro(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setCancelando(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#1D9E75] flex items-center justify-center text-white font-bold">G</div>
            <div>
              <p className="text-sm font-bold text-gray-900 leading-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>GuiaCit</p>
              <p className="text-[11px] text-gray-500 leading-tight">Painel do Anunciante</p>
            </div>
          </Link>
          <button onClick={sair} data-testid="anunc-logout-btn" className="text-xs text-gray-600 hover:text-red-600 flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-red-50">
            <LogOut size={13} /> Sair
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        <StatusBanner
          user={user}
          onAssinar={handleAssinar}
          onCancelar={handleCancelarAssinatura}
          criandoAssinatura={criandoAssinatura}
          cancelando={cancelando}
        />

        {aguardandoConfirmacao && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-start gap-3" data-testid="anunc-aguardando-confirmacao">
            <Loader2 size={18} className="text-blue-600 mt-0.5 flex-shrink-0 animate-spin" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-blue-900">Aguardando confirmação do Mercado Pago…</p>
              <p className="text-xs text-blue-800 mt-0.5">Isso pode levar alguns segundos. Não feche esta página.</p>
            </div>
          </div>
        )}

        {ok && (
          <div className="bg-[#E1F5EE] border border-[#1D9E75]/30 text-[#0F6E56] text-sm rounded-2xl px-4 py-3" data-testid="anunc-toast-ok">{ok}</div>
        )}
        {erro && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-2xl px-4 py-3" data-testid="anunc-toast-erro">{erro}</div>
        )}

        <section className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#E1F5EE] flex items-center justify-center text-[#1D9E75]">
              <Building2 size={18} />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: "'Work Sans', sans-serif" }}>Dados da empresa</h2>
              <p className="text-xs text-gray-500">Mantenha suas informações atualizadas</p>
            </div>
          </div>

          <form onSubmit={salvar} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Nome fantasia *</label>
                <input required value={form.nomeFantasia} onChange={set('nomeFantasia')} data-testid="anunc-dash-nome-input" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Razão social</label>
                <input value={form.razaoSocial} onChange={set('razaoSocial')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">CNPJ</label>
                <input value={form.cnpj} onChange={set('cnpj')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Tipo de empresa</label>
                <select value={form.tipoEmpresa} onChange={set('tipoEmpresa')} data-testid="anunc-dash-tipo-select" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1D9E75]">
                  <option value="">— Selecione —</option>
                  {tipos.map(t => <option key={t.id} value={t.nome}>{t.nome}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">WhatsApp</label>
                <input value={form.whatsapp} onChange={set('whatsapp')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Site</label>
                <input value={form.site} onChange={set('site')} placeholder="https://" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">CEP</label>
                <input value={form.cep} onChange={set('cep')} onBlur={onCepBlur} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1">Rua</label>
                <input value={form.rua} onChange={set('rua')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Número</label>
                <input value={form.numero} onChange={set('numero')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Complemento</label>
                <input value={form.complemento} onChange={set('complemento')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Bairro</label>
                <input value={form.bairro} onChange={set('bairro')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1">Cidade</label>
                <input value={form.cidade} onChange={set('cidade')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">UF</label>
                <input maxLength={2} value={form.estado} onChange={set('estado')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75] uppercase" />
              </div>
            </div>

            {erro && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2" data-testid="anunc-form-erro">{erro}</div>}

            <button type="submit" disabled={salvando} data-testid="anunc-dash-salvar-btn" className="bg-[#1D9E75] hover:bg-[#178460] text-white font-medium rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 disabled:opacity-50">
              {salvando ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
            </button>
          </form>
        </section>

        <section className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="anunc-anuncios-section">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
              <ExternalLink size={18} />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: "'Work Sans', sans-serif" }}>Meus anúncios</h2>
              <p className="text-xs text-gray-500">1º anúncio grátis por 14 dias · depois Card R$ 39 / Slider R$ 79</p>
            </div>
          </div>
          <div className="text-center py-8 text-sm text-gray-400 border border-dashed border-gray-200 rounded-xl mt-3">
            Em breve · Disponível na próxima atualização
          </div>
        </section>
      </main>
    </div>
  );
}
